import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiSend } from '../lib/api'
import { formatDateTime } from '../lib/format'
import type { ConfigBackup, ConfigDiff } from '../lib/types'
import { EmptyState, ErrorBox, Loading } from '../components/Feedback'
import { Richtlinien } from '../components/Richtlinien'
import type { Customer } from '../lib/types'

interface Props {
  customerSlug: string | null
}

const TRIGGER_LABEL: Record<ConfigBackup['trigger'], string> = {
  zeitplan: 'Zeitplan',
  syslog: 'Admin-Login (Syslog)',
  manuell: 'von Hand',
}

/** Der Prüfstatus als Text, wie ihn die Versionsliste im Entwurf zeigt. */
export function reviewLabel(b: ConfigBackup): string {
  switch (b.review_state) {
    case 'erstsicherung':
      return 'Erstsicherung'
    case 'geprueft':
      return b.reviewed_by_name ? `geprüft von ${b.reviewed_by_name}` : 'geprüft'
    case 'beanstandet':
      return b.reviewed_by_name ? `beanstandet von ${b.reviewed_by_name}` : 'beanstandet'
    default:
      return b.changed ? 'ungeprüft' : 'ohne Änderung'
  }
}

/**
 * Config-Backups: links die Versionen, rechts der Unterschied zur vorigen
 * Version mit den automatischen Hinweisen (docs/design/screens/ConfigDiff.dc.html).
 */
export function ConfigsPage({ customerSlug }: Props) {
  const [ansicht, setAnsicht] = useState<'versionen' | 'richtlinien'>('versionen')
  const customers = useQuery({
    queryKey: ['customers'],
    queryFn: ({ signal }) => apiGet<Customer[]>('/customers', signal),
    staleTime: 60_000,
    enabled: Boolean(customerSlug),
  })
  const customer = customers.data?.find((c) => c.slug === customerSlug)

  return (
    <>
      <div role="tablist" aria-label="Ansicht" className="reiterleiste">
        {(
          [
            ['versionen', 'Versionen'],
            ['richtlinien', 'Richtlinien'],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={ansicht === key}
            className="reiter"
            onClick={() => setAnsicht(key)}
          >
            {label}
          </button>
        ))}
      </div>
      {ansicht === 'versionen' ? (
        <Versionen customerSlug={customerSlug} />
      ) : customerSlug && !customer ? (
        <Loading what="Den Kunden" />
      ) : (
        <>
          <div className="page-head">
            <div>
              <h1 className="page-title">Richtlinien</h1>
              <div className="page-sub">{customer?.name ?? 'Alle Kunden'} · Prüfung jeder Config-Sicherung</div>
            </div>
          </div>
          <Richtlinien customer={customer} />
        </>
      )}
    </>
  )
}

/** Die Versionsliste mit Unterschied und Prüfstatus. */
function Versionen({ customerSlug }: Props) {
  const queryClient = useQueryClient()
  const [selected, setSelected] = useState<string | null>(null)
  const [onlyChanges, setOnlyChanges] = useState(true)

  const list = useQuery({
    queryKey: ['configs', customerSlug],
    queryFn: ({ signal }) => apiGet<ConfigBackup[]>('/configs?anzahl=200', signal),
    refetchInterval: 60_000,
  })

  // Ohne Auswahl steht die neueste Änderung offen, weil sie der Grund ist,
  // warum jemand diese Seite öffnet.
  useEffect(() => {
    if (selected || !list.data || list.data.length === 0) return
    const firstOpen = list.data.find((b) => b.changed && b.review_state === 'offen')
    const fallback = firstOpen ?? list.data[0]
    if (fallback) setSelected(fallback.id)
  }, [list.data, selected])

  const diff = useQuery({
    queryKey: ['config-diff', selected, onlyChanges],
    queryFn: ({ signal }) =>
      apiGet<ConfigDiff>(
        `/configs/${selected}/diff${onlyChanges ? '?nur=aenderungen' : ''}`,
        signal,
      ),
    enabled: selected !== null,
  })

  const review = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'geprueft' | 'beanstandet' }) =>
      apiSend<ConfigBackup>('POST', `/configs/${id}/review`, { status }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['configs'] })
      void queryClient.invalidateQueries({ queryKey: ['config-diff'] })
      void queryClient.invalidateQueries({ queryKey: ['alerts'] })
    },
  })

  if (list.isError) return <ErrorBox error={list.error} what="Die Sicherungen" />
  if (list.isPending) return <Loading what="Sicherungen" />

  const current = list.data.find((b) => b.id === selected)
  const versions = current ? list.data.filter((b) => b.device_id === current.device_id) : []
  const open = list.data.filter((b) => b.changed && b.review_state === 'offen')

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Config-Backups</h1>
          <div className="page-sub">
            {customerSlug ?? 'Alle Kunden'} · {list.data.length} Versionen, {open.length} ungeprüft
          </div>
        </div>
        {current ? (
          <div className="row" style={{ gap: 8 }}>
            <a className="btn" href={`/api/configs/${current.id}/inhalt`}>
              Version herunterladen
            </a>
            {current.changed && current.review_state === 'offen' ? (
              <>
                <button
                  type="button"
                  className="btn"
                  disabled={review.isPending}
                  onClick={() => review.mutate({ id: current.id, status: 'beanstandet' })}
                >
                  Beanstanden
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={review.isPending}
                  onClick={() => review.mutate({ id: current.id, status: 'geprueft' })}
                >
                  Als geprüft markieren
                </button>
              </>
            ) : null}
          </div>
        ) : null}
      </div>

      {list.data.length === 0 ? (
        <EmptyState
          title="Noch keine Sicherung"
          hint="Sobald ein Gerät einen SSH-Zugang im Tresor hat, wird es nach Zeitplan gesichert. Auf der Geräteseite lässt sich eine Sicherung auch sofort auslösen."
        />
      ) : (
        <div className="row" style={{ alignItems: 'flex-start', gap: 16 }}>
          <section className="card" style={{ width: 340, flexShrink: 0 }}>
            <div className="card-head">
              <h2 className="card-title">Versionen</h2>
            </div>
            <div className="stack" style={{ gap: 0 }}>
              {(current ? versions : list.data).map((b) => (
                <button
                  key={b.id}
                  type="button"
                  className="version"
                  aria-current={b.id === selected}
                  onClick={() => setSelected(b.id)}
                  style={
                    b.id === selected
                      ? { background: 'var(--acc-soft)', borderLeftColor: 'var(--acc)' }
                      : undefined
                  }
                >
                  <span className="row" style={{ justifyContent: 'space-between' }}>
                    <span className="mono">{formatDateTime(b.taken_at)}</span>
                    <span className="row" style={{ gap: 6 }}>
                      {b.added > 0 ? <span style={{ color: 'var(--ok)' }}>+{b.added}</span> : null}
                      {b.removed > 0 ? (
                        <span style={{ color: 'var(--crit)' }}>−{b.removed}</span>
                      ) : null}
                    </span>
                  </span>
                  <span
                    className="muted"
                    style={{
                      fontSize: 12,
                      color:
                        b.review_state === 'offen' && b.changed ? 'var(--warn)' : 'var(--muted)',
                    }}
                  >
                    {b.device_name} · {reviewLabel(b)}
                  </span>
                </button>
              ))}
            </div>
          </section>

          <div className="stack grow" style={{ minWidth: 0 }}>
            {current ? (
              <>
                <section className="card">
                  <div className="card-head">
                    <h2 className="card-title">
                      {current.device_name} · {current.customer_name}
                    </h2>
                    <span className="muted" style={{ fontSize: 12 }}>
                      {current.error
                        ? 'Sicherung fehlgeschlagen'
                        : `${TRIGGER_LABEL[current.trigger]} · ${current.line_count} Zeilen`}
                    </span>
                  </div>
                  <div className="card-body stack" style={{ gap: 10 }}>
                    <span>
                      Erkannt {formatDateTime(current.taken_at)} · Auslöser:{' '}
                      {TRIGGER_LABEL[current.trigger]} · {current.method === 'http' ? 'über die API' : 'per SSH'}
                      {current.review_state === 'geprueft' || current.review_state === 'beanstandet'
                        ? ` · ${reviewLabel(current)}`
                        : ''}
                    </span>
                    {current.error ? (
                      <p className="badge badge-crit" style={{ height: 'auto', padding: '8px 10px' }}>
                        {current.error}
                      </p>
                    ) : null}
                    {(current.hinweise ?? []).length > 0 ? (
                      <div className="stack" style={{ gap: 8 }}>
                        <strong>Automatische Hinweise zu dieser Änderung</strong>
                        {(current.hinweise ?? []).map((h, i) => (
                          <div
                            key={`${h.regel}-${i}`}
                            className="hinweis"
                            style={{
                              background: h.schwere === 'crit' ? 'var(--crit-bg)' : 'var(--warn-bg)',
                            }}
                          >
                            <strong
                              style={{
                                color: h.schwere === 'crit' ? 'var(--crit)' : 'var(--warn)',
                              }}
                            >
                              {h.titel}
                            </strong>
                            {h.detail ? <span>{h.detail}</span> : null}
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </section>

                <section className="card">
                  <div className="card-head">
                    <h2 className="card-title">
                      {diff.data?.alt
                        ? `${formatDateTime(diff.data.alt.taken_at)} → ${formatDateTime(current.taken_at)}`
                        : 'Erstsicherung'}
                    </h2>
                    <div className="row" style={{ gap: 6 }}>
                      <button
                        type="button"
                        className="btn btn-sm"
                        aria-pressed={onlyChanges}
                        onClick={() => setOnlyChanges(true)}
                      >
                        Nur Änderungen
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm"
                        aria-pressed={!onlyChanges}
                        onClick={() => setOnlyChanges(false)}
                      >
                        Ganze Datei
                      </button>
                    </div>
                  </div>
                  {diff.isPending ? (
                    <div className="card-body">
                      <Loading what="Unterschiede" />
                    </div>
                  ) : diff.isError ? (
                    <ErrorBox error={diff.error} what="Der Vergleich" />
                  ) : (diff.data?.zeilen.length ?? 0) === 0 ? (
                    <div className="empty">
                      <span>Keine Unterschiede zur vorigen Version.</span>
                    </div>
                  ) : (
                    <pre className="diff">
                      {diff.data?.zeilen.map((line, i) => (
                        <div
                          key={`${line.nr}-${i}`}
                          className={`diff-line diff-${line.art}`}
                        >
                          <span className="diff-nr">{line.nr}</span>
                          <span className="diff-sign">
                            {line.art === 'neu' ? '+' : line.art === 'weg' ? '−' : ' '}
                          </span>
                          <span>{line.text}</span>
                        </div>
                      ))}
                    </pre>
                  )}
                </section>
              </>
            ) : null}
          </div>
        </div>
      )}
    </>
  )
}
